import React, { useState, useEffect, useCallback } from 'react';
import MobileBottomNav from '../components/layout/MobileBottomNav';
import "./LandingPage.css";
import { FaEnvelope, FaCommentDots, FaLocationArrow, FaTimes ,FaEye} from "react-icons/fa";
import { AUTH_STORAGE_KEY } from '../socket';
import CommonNavbar from "../components/CommonNavbar";
import { useNavigate } from "react-router-dom";
import AdvertisementSlider from "../components/AdvertisementSlider";
import CategoryGroupSection from "../components/CategoryGroupSection";
import LatestBlogsSection from "../components/LatestBlogsSection";
import LatestVideosSection from "../components/LatestVideosSection";
import FloatingSideButtons from "../components/FloatingSideButtons";
import FooterPage from "../components/Footer";

// ── Icon mapping for categories ────────────────────────────────────────────────
const CATEGORY_ICONS = {
  // Home Services
  plumber: "💧",
  plumbing: "💧",
  electrician: "⚡",
  electrical: "⚡",
  carpenter: "🪵",
  carpentry: "🪵",
  painter: "🎨",
  painting: "🎨",
  pest: "🐜",
  "pest control": "🐜",
  cleaning: "🧹",
  housekeeping: "🧹",

  // AC & Appliances
  ac: "❄️",
  "ac repair": "❄️",
  hvac: "❄️",
  appliance: "📦",
  refrigerator: "🧊",

  // Beauty & Wellness
  salon: "💇",
  salons: "💇",
  "beauty parlour": "💄",
  "beauty parlours": "💄",
  beauty: "💄",
  spa: "🧖",
  massage: "🧖",
  wellness: "🧖",
  gym: "🏋️",
  gyms: "🏋️",
  fitness: "🏋️",
  yoga: "🧘",
  "yoga classes": "🧘",

  // Medical
  hospital: "🏥",
  hospitals: "🏥",
  dentist: "🦷",
  dentists: "🦷",
  cardiologist: "❤️",
  cardiologists: "❤️",
  neurologist: "🧠",
  neurologists: "🧠",
  dermatologist: "🩺",
  dermatologists: "🩺",
  gynaecologist: "👩‍⚕️",
  gynaecologists: "👩‍⚕️",
  ent: "👂",
  sexologist: "🩺",
  diagnostic: "🔬",

  // Education
  school: "🏫",
  schools: "🏫",
  college: "🎓",
  colleges: "🎓",
  coaching: "📚",
  tutor: "📚",
  training: "📚",
  institute: "📖",

  // Restaurants & Food
  restaurant: "🍽️",
  restaurants: "🍽️",
  cafe: "☕",
  cafes: "☕",
  bakery: "🥐",
  bakeries: "🥐",
  caterer: "🍱",
  caterers: "🍱",
  fastfood: "🍔",
  "fast food": "🍔",
  hotel: "🏨",
  hotels: "🏨",

  // Travel
  travel: "✈️",
  "travel agents": "✈️",
  tour: "🧳",
  "tour operators": "🧳",
  visa: "🛂",
  resort: "🏖️",
  resorts: "🏖️",

  // Automotive
  car: "🚗",
  cars: "🚗",
  "car dealer": "🚗",
  "car dealers": "🚗",
  "car rental": "🚘",
  "car rentals": "🚘",
  mechanic: "🔧",
  bike: "🏍️",
  "bike dealer": "🏍️",
  tyre: "🛞",
  "service centre": "🔧",

  // Technology
  website: "🌐",
  "website design": "🌐",
  software: "💻",
  computer: "💻",
  android: "📱",
  app: "📱",
  developer: "👨‍💻",
  cctv: "📹",
  security: "🛡️",
  "digital marketing": "📈",

  // Business & Professional
  architect: "📐",
  auditor: "📊",
  accountant: "🧮",
  "chartered accountant": "🧮",
  ca: "🧮",
  advocate: "⚖️",
  lawyer: "⚖️",
  legal: "⚖️",
  consultant: "💼",
  tax: "💰",

  // Real Estate
  property: "🏢",
  builder: "🏗️",
  builders: "🏗️",
  contractor: "🏗️",
  estate: "🏠",
  realtor: "🏠",
  commercial: "🏬",

  // Shopping
  jewellery: "💍",
  clothing: "👕",
  furniture: "🛋️",
  mobile: "📱",
  book: "📚",
  mall: "🛍️",

  // Events
  wedding: "💒",
  photographer: "📸",
  photography: "📸",
  planner: "🎉",
  event: "🎊",
  banquet: "🏛️",

  // Sports
  sports: "⚽",
  club: "🏆",
  swimming: "🏊",
  martial: "🥋",

  // Advertising & Marketing
  advertising: "📢",
  newspaper: "📰",
  marketing: "📈",

  // Agriculture
  agri: "🌾",
  agriculture: "🌾",
  farmer: "🌱"
};

function getIconForCategory(name) {
  const key = String(name || "").trim().toLowerCase();
  
  // Direct match first (exact or substring)
  for (const [pattern, icon] of Object.entries(CATEGORY_ICONS)) {
    if (key === pattern || key.includes(pattern)) {
      return icon;
    }
  }
  
  // Fallback: return default icon
  return "🔧";
}



function normalizeCategory(cat) {
  const raw = String(cat || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("plumb")) return "plumbing";
  if (raw.includes("electric")) return "electrical";
  if (raw.includes("carpen")) return "carpentry";
  return raw;
}

function mapCategory(cat) {
  const key = normalizeCategory(cat);
  if (key === "plumbing") return "Plumbing";
  if (key === "electrical") return "Electrical";
  if (key === "carpentry") return "Carpentry";
  return String(cat || "").trim();
}

function getToken() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw)?.token : null;
  } catch { return null; }
}

function authHeader() {
  const token = getToken();
  return token ? { Authorization: "Bearer " + token, "Content-Type": "application/json" } : {};
}

async function createLead(serviceId) {
  if (!serviceId) return;
  try {
    const now = new Date();
    const scheduledDate = now.toISOString().slice(0, 10);
    const scheduledTime = now.toTimeString().slice(0, 5);   // HH:MM
    await fetch("/api/bookings", {
      method: "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body: JSON.stringify({
        serviceId,
        scheduledDate,
        scheduledTime,
        notes: "Lead created from service listing",
      }),
    });
  } catch (err) {
    console.warn("Failed to create lead", err);
  }
}

async function savePredefinedChatMessage(toEmail, serviceName) {
  if (!toEmail) return;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const currentUser = parsed?.user?.email?.toLowerCase() || "";
    if (!currentUser) return;

    const predefinedMessage = `Hi! I'm interested in your ${serviceName || "service"}. Can we discuss details?`;

    await fetch("/save-message", {          // ← was /api/chats/save-message
      method: "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body: JSON.stringify({
        from:      currentUser,
        to:        toEmail,
        text:      predefinedMessage,
        type:      "text",
        statusTag: "open",
        time:      Date.now(),
      }),
    });
  } catch (err) {
    console.warn("Failed to save predefined message", err);
  }
}

// ── Haversine distance in km ──────────────────────────────────────────────────
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ 
}) {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const data = localStorage.getItem(AUTH_STORAGE_KEY);
    if (data) setUser(JSON.parse(data).user);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setUser(null);
    navigate("/");
  };

  const goProtected = (path) => {
    if (!user) return navigate("/login");
    navigate(path);
    setMenuOpen(false);
  };
   
}

// ── HeroSlider ────────────────────────────────────────────────────────────────
function HeroSlider({ onSearch }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCurrent((c) => (c + 1) % 3), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    
    <section className="eb-hero">
      {[0, 1, 2].map((i) => (
        <div key={i} className={`eb-slide eb-slide-${i + 1}${current === i ? " active" : ""}`} />
      ))}
      <div className="eb-hero-content">
        <div className="eb-hero-tag">✦ &nbsp; India's Trusted Service Network</div>
        <h1 className="eb-hero-title">
          Find <span>Trusted Services</span> Near You
        </h1>
        <p className="eb-hero-sub">
          Connect with verified local professionals for every need — from home repairs to business solutions, all in one place.
        </p>
      </div>
      <div className="eb-slide-dots">
        {[0, 1, 2].map((i) => (
          <button key={i} className={`eb-dot${current === i ? " active" : ""}`} onClick={() => setCurrent(i)} />
        ))}
      </div>
    </section>
  );
}

// ── Categories ────────────────────────────────────────────────────────────────
function Categories({ onFilter, categories = [] }) {
  const [categorySearch, setCategorySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [displayCount, setDisplayCount] = useState(14);

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );


  const visibleCategories = filteredCategories.slice(0, displayCount);
  const hasMore = filteredCategories.length > displayCount;

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleViewMore = () => {
    setDisplayCount(prev => prev + 14);
  };

  return (
    <section className="eb-section">
      <div className="eb-section-head">
        <h2 className="eb-section-title" style={{color:'black !important'}}>What are you looking for?</h2>
        <p className="eb-section-sub">
          Explore our wide range of trusted service categories across India.
        </p>

        {/* Category Search */}
        <div style={{ maxWidth: "450px", margin: "20px auto 0" }}>
          <input
            type="text"
            placeholder="🔍 Search Category..."
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            className="eb-category-search"
          />
        </div>
      </div>

      <div className="eb-cats-grid">
        {visibleCategories.length > 0 ? (
          visibleCategories.map((c) => {
            const isExpanded = expandedCategories[c.id || c.name];
           return (
  <div
    key={c.id || c.name}
    className={`eb-cat-card ${isExpanded ? "expanded" : ""}`}
    onClick={() => onFilter(c.name)}
    style={{ cursor: "pointer" }}
  >
    <span className="eb-cat-icon">
      {c.icon || getIconForCategory(c.name)}
    </span>

    <div className="eb-cat-name">{c.name}</div>
{/* 
    <div className="eb-cat-count">
      {c.serviceCount || 0} providers
    </div> */}

    {/* Description - shown when expanded */}
    {isExpanded && c.description && (
      <div className="eb-cat-desc">{c.description}</div>
    )}

    {/* View More / Show Less */}
    <div className="eb-cat-actions">
      {c.description && (
        <button
          className="eb-cat-toggle-btn"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            toggleExpand(c.id || c.name);
          }}
        >
          {isExpanded ? "Show Less ▲" : "View More ▼"}
        </button>
      )}
    </div>
  </div>
);
          })
        ) : (
          <div style={{ textAlign: "center", width: "100%" }}>
            No category found
          </div>
        )}
      </div>

      {/* View More Button - Bottom Right */}
      {hasMore && (
        <div className="eb-cats-view-more-container">
          <button className="eb-cats-view-more-btn" onClick={handleViewMore}>
            View More Categories →
          </button>
        </div>
      )}
    </section>
  );
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
const isLoggedIn = () => !!localStorage.getItem(AUTH_STORAGE_KEY);

function slugifyBusinessPath(value) {
  return String(value || "business")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    || "business";
}

// ── ServiceCard ───────────────────────────────────────────────────────────────
function ServiceCard({ s, setVoiceCall, userLocation }) {
  const navigate = useNavigate();
  const [imageIndex, setImageIndex] = useState(0);

  const galleryImages = React.useMemo(() => {
    const businessImage = s.img || "";
    const catalogImages = (Array.isArray(s.catalogItems) ? s.catalogItems : [])
      .filter((item) => {
        const status = String(item?.approvalStatus || item?.status || "").toLowerCase();
        if (status === "pending" || status === "rejected") return false;
        if (item?.isApproved === false) return false;
        return true;
      })
      .map((item) => item.photoUrl || item.image || "")
      .filter(Boolean);
    return [businessImage, ...catalogImages].filter(Boolean);
  }, [s]);

  useEffect(() => {
    if (galleryImages.length <= 1) return;
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [galleryImages.length]);

  // Compute distance if we have both coords
  const distanceKm =
    userLocation && s.lat && s.lng
      ? haversineKm(userLocation.lat, userLocation.lng, parseFloat(s.lat), parseFloat(s.lng))
      : null;

       const recordInteraction = useCallback(async () => {
    if (!isLoggedIn()) return;
    await Promise.allSettled([
      createLead(s._id),
      savePredefinedChatMessage(s.ownerId, s.businessName || s.name),
    ]);
  }, [s._id, s.ownerId, s.businessName, s.name]);

const handleMail = async (e) => {
    e?.stopPropagation();
    if (!isLoggedIn()) { navigate("/login"); return; }
    await recordInteraction();
    navigate("/kmail", { state: { composeTo: s.ownerId } });
  };

  const handleChat = async (e) => {
    e?.stopPropagation();
    if (!isLoggedIn()) { navigate("/login"); return; }
    await recordInteraction();
    navigate("/kmail/Krivachat", { state: { email: s.ownerId } });
  };
  const handleViewBusiness = () => {
    if (isLoggedIn()) {
      createLead(s._id).catch(() => {});
      savePredefinedChatMessage(s.ownerId, s.name).catch(() => {});
    }

    const businessSlug = slugifyBusinessPath(s.businessName || s.name || "business");
    navigate(`/${businessSlug}-${s._id}`);
  };

  return (
    <div className="eb-service-card" onClick={handleViewBusiness} style={{ cursor: "pointer" }}>
      <div className="eb-service-slider" onClick={(e) => e.stopPropagation()}>
        <div className="eb-service-slider-track" style={{ transform: `translateX(-${imageIndex * 100}%)` }}>
          {galleryImages.map((img, index) => (
            <div key={`${s._id}-${index}`} className="eb-service-slide">
              <img src={img} alt={`${s.name} image ${index + 1}`} onError={(e) => (e.target.style.display = "none")} />
            </div>
          ))}
        </div>
        {galleryImages.length > 1 && (
          <>
            <button
              type="button"
              className="eb-slider-dot eb-slider-dot-left"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              className="eb-slider-dot eb-slider-dot-right"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((prev) => (prev + 1) % galleryImages.length);
              }}
              aria-label="Next image"
            >
              ›
            </button>
            <div className="eb-slider-dots">
              {galleryImages.map((_, index) => (
                <button
                  key={`${s._id}-dot-${index}`}
                  type="button"
                  className={`eb-slider-indicator${imageIndex === index ? " active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageIndex(index);
                  }}
                  aria-label={`Show image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="eb-service-body">
                {s.badge && <div className="eb-service-badge">{s.badge}</div>}

        {s.businessName ? (
          <div className="eb-service-cat">{mapCategory(s.category)}</div>
        ) : null}
        <div className="eb-service-name">
          {s.businessName ? s.name : mapCategory(s.category) || "Service"}
        </div>
        <div className="eb-service-desc">{s.description}</div>

        <div className="eb-service-footer">
          <div className="eb-service-meta">
            <div className="eb-service-area">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1a4 4 0 0 1 4 4c0 3-4 6-4 6S2 8 2 5a4 4 0 0 1 4-4z" stroke="#888" strokeWidth="1.3" />
                <circle cx="6" cy="5" r="1.3" stroke="#888" strokeWidth="1.1" />
              </svg>
             {s.area?.length > 50 ? `${s.area.slice(0, 50)}...` : s.area}
            </div>
            {/* ── Distance badge ── */}
            {distanceKm !== null && (
              <div style={distanceBadgeStyle(distanceKm)}>
                📍 {distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(1)} km`} away
              </div>
            )}
            
            <div className="eb-service-area"><svg width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path
    d="M3.1 1.8C3.1 1.4 3.4 1.1 3.8 1.1H4.8C5.1 1.1 5.4 1.3 5.5 1.6L5.9 3C6 3.3 5.9 3.6 5.7 3.8L5.1 4.4C5.6 5.5 6.5 6.4 7.6 6.9L8.2 6.3C8.4 6.1 8.7 6 9 6.1L10.4 6.5C10.7 6.6 10.9 6.9 10.9 7.2V8.2C10.9 8.6 10.6 8.9 10.2 8.9C6.3 8.9 3.1 5.7 3.1 1.8Z"
    stroke="#888"
    strokeWidth="1.1"
    strokeLinejoin="round"
  />
</svg> {s.ownerId}</div>
          </div>
          {/* bottom actions grouped with footer so footer + actions stay anchored together */}
          <div className="eb-service-actions" style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={(e) => { e.stopPropagation(); handleViewBusiness(); }} style={btnStyle("#6b7280", "#4b5563", "#fff")}> 
               <FaEye size={14} /> View
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleMail(); }} style={btnStyle("#6dd5ed", "#2193b0", "#fff")}> 
              <FaEnvelope size={14} /> Mail
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleChat(); }} style={btnStyle("#f7971e", "#ffd200", "#222")}> 
              <FaCommentDots size={14} /> Chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function distanceBadgeStyle(km) {
  const isNear = km <= 5;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 7px",
    borderRadius: 10,
    background: isNear ? "#e6f9f0" : "#f5f5f5",
    color: isNear ? "#1a7a4a" : "#666",
    border: `1px solid ${isNear ? "#a3e0c0" : "#ddd"}`,
    marginTop: 2,
  };
}

// ── StatsBar ──────────────────────────────────────────────────────────────────
function StatsBar() {
  const stats = [
    { num: "1,200+", label: "Verified Providers" },
    { num: "48+", label: "Service Categories" },
    { num: "38", label: "Cities Covered" },
    { num: "98%", label: "Customer Satisfaction" },
  ];
  return (
    <div className="eb-stats-bar">
      {stats.map((s) => (
        <div key={s.label} className="eb-stat-item">
          <span className="eb-stat-num">{s.num}</span>
          <span className="eb-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

const btnStyle = (c1, c2, textColor) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: `linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`,
  color: textColor,
  border: "none",
  borderRadius: 13,
  padding: "8px 12px",
  fontWeight: 600,
  fontSize: 13,
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

// ── ServicesSection ───────────────────────────────────────────────────────────
const RADIUS_OPTIONS = [2, 5, 10, 25, 50];

function ServicesSection({
  services,
  loading,
  activeFilter,
  setActiveFilter,
  searchQuery,
  setVoiceCall,
  userLocation,
  locationLoading,
  locationError,
  onRequestLocation,
  onClearLocation,
  nearbyRadius,
  setNearbyRadius,
}) {
  const [sort, setSort] = useState("default");

  // When location is active, default sort to distance
  useEffect(() => {
    if (userLocation) setSort("distance");
    else if (sort === "distance") setSort("default");
  }, [userLocation]);

  // Filter by category + search text
  let list = services.filter((s) => {
    const categories = String(s.category || "").split(",").map((c) => normalizeCategory(c)).filter(Boolean);
    const filterKey = normalizeCategory(activeFilter);
    const matchFilter = activeFilter === "All" || categories.includes(filterKey);
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q || [s.category, s.businessName, s.name, s.description, s.area, s.phone].some((f) =>
        String(f || "").toLowerCase().includes(q)
      );
    return matchFilter && matchSearch;
  });

  // Filter by proximity radius when location is active
  if (userLocation) {
    list = list.filter((s) => {
      if (!s.lat || !s.lng) return true; // include services without coords
      const d = haversineKm(userLocation.lat, userLocation.lng, parseFloat(s.lat), parseFloat(s.lng));
      return d <= nearbyRadius;
    });
  }

  // Sort
  if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
  if (sort === "area")   list = [...list].sort((a, b) => a.area.localeCompare(b.area));
  if (sort === "distance" && userLocation) {
    list = [...list].sort((a, b) => {
      const da = a.lat && a.lng ? haversineKm(userLocation.lat, userLocation.lng, parseFloat(a.lat), parseFloat(a.lng)) : Infinity;
      const db = b.lat && b.lng ? haversineKm(userLocation.lat, userLocation.lng, parseFloat(b.lat), parseFloat(b.lng)) : Infinity;
      return da - db;
    });
  }

  return (
    <section className="eb-section eb-services-section" id="services">
      <div className="eb-services-toolbar">
        <div>
          <h2 className="eb-section-title blackcolor" style={{ marginBottom: 0,color:'black !important' }}>Available Businesses</h2>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
       

          {/* ── Near Me button ── */}
          <NearMeButton
            userLocation={userLocation}
            locationLoading={locationLoading}
            locationError={locationError}
            onRequestLocation={onRequestLocation}
            onClearLocation={onClearLocation}
            nearbyRadius={nearbyRadius}
            setNearbyRadius={setNearbyRadius}
          />

          {/* ── Sort selector ── */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={selectStyle}
          >
            <option value="default">Sort: Default</option>
            {userLocation && <option value="distance">Nearest first</option>}
            <option value="rating">Top rated</option>
            <option value="area">By area</option>
          </select>
        </div>
      </div>

      {/* Location status strip */}
      {userLocation && (
        <div style={locationStripStyle}>
          <FaLocationArrow size={12} color="#1a7a4a" />
          <span>
            Showing services within <strong>{nearbyRadius} km</strong> of your location
            {list.length === 0 && " — no results, try increasing the radius"}
          </span>
        </div>
      )}
      {locationError && (
        <div style={{ ...locationStripStyle, background: "#fff3f3", borderColor: "#f5c6c6", color: "#c0392b" }}>
          ⚠️ {locationError}
        </div>
      )}

      <div className="eb-services-grid">
        {loading ? (
          <div>Loading services...</div>
        ) : list.length === 0 ? (
          <div>No services found</div>
        ) : (
          list.map((s) => (
            <ServiceCard
              key={s._id}
              s={s}
              setVoiceCall={typeof setVoiceCall !== "undefined" ? setVoiceCall : null}
              userLocation={userLocation}
            />
          ))
        )}
      </div>
    </section>
  );
}

// ── Near Me button + radius picker ───────────────────────────────────────────
function NearMeButton({ userLocation, locationLoading, locationError, onRequestLocation, onClearLocation, nearbyRadius, setNearbyRadius }) {
  if (locationLoading) {
    return (
      <button style={nearMeBtnStyle(false, true)} disabled>
        <FaLocationArrow size={13} /> Locating…
      </button>
    );
  }

  if (userLocation) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button style={nearMeBtnStyle(true, false)} title="Location active">
          <FaLocationArrow size={13} /> Near Me
        </button>

        {/* Radius picker */}
        <select
          value={nearbyRadius}
          onChange={(e) => setNearbyRadius(Number(e.target.value))}
          style={{ ...selectStyle, padding: "5px 8px" }}
          title="Search radius"
        >
          {RADIUS_OPTIONS.map((r) => (
            <option key={r} value={r}>{r} km</option>
          ))}
        </select>

        {/* Clear location */}
        <button
          onClick={onClearLocation}
          title="Clear location filter"
          style={{
            background: "none",
            border: "1px solid #ddd",
            borderRadius: 20,
            padding: "5px 9px",
            cursor: "pointer",
            color: "#888",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <FaTimes size={11} /> Clear
        </button>
      </div>
    );
  }

  return (
    <button style={nearMeBtnStyle(false, false)} onClick={onRequestLocation} title="Find services near you">
      <FaLocationArrow size={13} /> Near Me
    </button>
  );
}

const nearMeBtnStyle = (active, loading) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: active ? "linear-gradient(90deg,#1a7a4a,#27ae60)" : "linear-gradient(90deg,#E8820C,#f5a623)",
  color: "#fff",
  border: "none",
  borderRadius: 20,
  padding: "6px 14px",
  fontWeight: 600,
  fontSize: 13,
  cursor: loading ? "wait" : "pointer",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  opacity: loading ? 0.7 : 1,
  transition: "all 0.2s ease",
});

const selectStyle = {
  border: "1px solid #ddd",
  borderRadius: 20,
  padding: "5px 12px",
  fontSize: 13,
  cursor: "pointer",
  background: "#fff",
  color: "#333",
};

const locationStripStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "#1a7a4a",
  background: "#e6f9f0",
  border: "1px solid #a3e0c0",
  borderRadius: 8,
  padding: "8px 14px",
  marginBottom: 16,
};

// ── WhyUs ─────────────────────────────────────────────────────────────────────
function WhyUs() {
  const cards = [
    { icon: "🛡️", title: "Verified Providers", text: "Every service provider goes through a thorough background check and skill verification process." },
    { icon: "⚡", title: "Instant Booking", text: "Book services in seconds. Confirm availability and schedule appointments in real time." },
    { icon: "💬", title: "Live Support", text: "Our support team is available 7 days a week to help resolve any issues quickly." },
    { icon: "🌐", title: "Nationwide Reach", text: "With coverage across 38+ cities, we bring quality services to every corner of India." },
  ];
  return (
    <section className="eb-section eb-why-section" id="why-us">
      <div className="eb-section-head">
        <div className="eb-section-label">Why E-Mail Bharat</div>
        <h2 style={{color:'white'}} className="eb-section-title">Built on Trust &amp; Reliability</h2>
        <p className="eb-section-sub">We make it easy to find, compare, and book services with confidence.</p>
      </div>
      <div className="eb-why-grid">
        {cards.map((c) => (
          <div key={c.title} className="eb-why-card">
            <div className="eb-why-icon">{c.icon}</div>
            <div className="eb-why-title">{c.title}</div>
            <p className="eb-why-text">{c.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
function CTA() {
  return (
    <section className="eb-cta-section" id="contact">
      <div className="eb-cta-inner">
        <div className="eb-section-label" style={{ textAlign: "center" }}>Get Started Today</div>
        <h2 className="eb-cta-title">Ready to Find the Perfect Service?</h2>
        <p className="eb-cta-text">Join thousands of satisfied customers who trust E-Mail Bharat for their daily service needs.</p>
        <a href="/login" className="eb-cta-btn">
          Register as Provider
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
    </section>
  );
}

// // ── Footer ────────────────────────────────────────────────────────────────────
// function Footer() {
//   return (
//     <footer className="eb-footer">
//       <span>E-Mail Bharat</span> &copy; {new Date().getFullYear()} &mdash; All rights reserved. Made with ♥ in India.
//     </footer>
//   );
// }

// ── Root Component ────────────────────────────────────────────────────────────
export default function LandingPage({ setVoiceCall = null }) {
  const [activeFilter, setActiveFilter]     = useState("All");
  const [searchQuery, setSearchQuery]       = useState("");
  const [services, setServices]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [categories, setCategories]         = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
const [categoryGroups, setCategoryGroups] = useState([]);
const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");

  // Location state
  const [userLocation, setUserLocation]     = useState(null);   // { lat, lng }
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError]   = useState(null);
  const [nearbyRadius, setNearbyRadius]     = useState(10);     // km

  // Fetch service categories
 useEffect(() => {
  const fetchCategories = async () => {
    try {
      const res = await fetch("/api/service-categories");
      const data = await res.json();

      // Categories (used in the top category grid)
      const mappedCategories = (data.categories || []).map((cat) => ({
        id: cat.id,
        name: cat.name,
        icon: getIconForCategory(cat.name),
        description: cat.description || "",
        serviceCount: cat.serviceCount || 0,
      }));

      setCategories(mappedCategories);

      // Groups (used in CategoryGroupSection)
      setCategoryGroups(data.groups || []);

    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
      setCategoryGroups([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  fetchCategories();
}, []);


 useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { fetchServices(); }, []);

  // useEffect(() => {
  //   if (!loading) console.log("Fetched services:", services);
  // }, [loading, services]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res  = await fetch('/api/allservices');
      const data = await res.json();

      const mapped = (data.services || []).map(s => ({
        _id:         s._id,
        category:    s.category,
        businessName: s.businessName || "",
        catalogItems: Array.isArray(s.catalogItems) ? s.catalogItems.filter((item) => {
          const status = String(item?.approvalStatus || item?.status || "").toLowerCase();
          if (status === "pending" || status === "rejected") return false;
          if (item?.isApproved === false) return false;
          return true;
        }) : [],
        name:        s.businessName || s.category || "Business",
        description: s.description,
        area:        s.area,
        phone:       s.phone,
        ownerId:     s.ownerId,
        // ── lat/lng from DB ──
        lat:         s.lat  ? parseFloat(s.lat)  : null,
        lng:         s.lng  ? parseFloat(s.lng)  : null,
        rating:      4.5,
        badge:       "Verified",
        img:         s.photoUrl || "https://via.placeholder.com/400",
      }));

      setServices(mapped);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };



const fetchIpLocation = useCallback(async () => {
  try {
    const res  = await fetch("https://ipapi.co/json/");
    const data = await res.json();

    if (data.latitude && data.longitude) {
      setUserLocation({ lat: data.latitude, lng: data.longitude });
      setLocationLoading(false);
      setLocationError(null);
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
    } else {
      throw new Error("No location in response");
    }
  } catch {
    setLocationLoading(false);
    setLocationError("Could not detect your location. Try searching by area name instead.");
  }
}, []);

const handleRequestLocation = useCallback(() => {
  if (!navigator.geolocation) {
    setLocationError("Geolocation is not supported by your browser.");
    return;
  }
  setLocationLoading(true);
  setLocationError(null);

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      setLocationLoading(false);
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
    },
    (err) => {
      // GPS failed → try IP-based location as fallback
      fetchIpLocation();
    },
    {
      timeout: 15000,
      maximumAge: 300000,       // accept cached position up to 5 min old
      enableHighAccuracy: false // false = faster, uses network/wifi not GPS
    }
  );
}, []);

  const handleClearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
  }, []);
  

  const handleCategoryFilter = (cat) => {
    setActiveFilter(cat);
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
     <FloatingSideButtons />
   <CommonNavbar
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      onSearch={setSearchQuery}
      userLocation={userLocation}
      locationLoading={locationLoading}
      locationError={locationError}
      onRequestLocation={handleRequestLocation}
      onClearLocation={handleClearLocation}
      nearbyRadius={nearbyRadius}
    />
      {!searchQuery && (
        <>
          {/* <HeroSlider onSearch={setSearchQuery} /> */}
          <AdvertisementSlider />
          <Categories onFilter={handleCategoryFilter} categories={categories} />
        </>
      )}

      {services.length === 0 && !loading && (
        <div style={{ color: 'red', textAlign: 'center', margin: 20 }}>
          No services found.
        </div>
      )}
<CategoryGroupSection groups={categoryGroups} />
      <ServicesSection
        services={services}
        loading={loading}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        searchQuery={searchQuery}
        setVoiceCall={setVoiceCall}
        // location props
        userLocation={userLocation}
        locationLoading={locationLoading}
        locationError={locationError}
        onRequestLocation={handleRequestLocation}
        onClearLocation={handleClearLocation}
        nearbyRadius={nearbyRadius}
        setNearbyRadius={setNearbyRadius}
      />


<LatestBlogsSection />

<LatestVideosSection />

      {!searchQuery && (
        <>
          <WhyUs />
          <StatsBar />
          <CTA />
          <FooterPage />
        </>
      )}
 {isMobile && <MobileBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />}

    </>
  );
}